import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateInNoteRequest,
    CreateOutNoteRequest,
    CreateWithdrawalNoteRequest,
    ListLotsParams,
    ListNotesParams,
    ListWithdrawalNotesParams,
    UpdateInNoteRequest,
    UpdateOutNoteRequest,
} from '@/lib/api/packaging-lot-api'
import { packagingLotApi } from '@/lib/api/packaging-lot-api'
import { m } from '@/lib/paraglide/messages'

// ============================================================================
// Query keys
// ============================================================================

export const packagingLotKeys = {
    all: ['packaging-lots'] as const,
    lots: () => [...packagingLotKeys.all, 'lots'] as const,
    lotList: (params: ListLotsParams) =>
        [...packagingLotKeys.lots(), 'list', params] as const,
    lotDetail: (id: string) =>
        [...packagingLotKeys.lots(), 'detail', id] as const,

    inNotes: () => [...packagingLotKeys.all, 'in-notes'] as const,
    inNoteList: (params: ListNotesParams) =>
        [...packagingLotKeys.inNotes(), 'list', params] as const,
    inNoteDetail: (id: string) =>
        [...packagingLotKeys.inNotes(), 'detail', id] as const,

    outNotes: () => [...packagingLotKeys.all, 'out-notes'] as const,
    outNoteList: (params: ListNotesParams) =>
        [...packagingLotKeys.outNotes(), 'list', params] as const,
    outNoteDetail: (id: string) =>
        [...packagingLotKeys.outNotes(), 'detail', id] as const,
    withdrawalNotes: () =>
        [...packagingLotKeys.all, 'withdrawal-notes'] as const,
    withdrawalNoteList: (params: ListWithdrawalNotesParams) =>
        [...packagingLotKeys.withdrawalNotes(), 'list', params] as const,
    withdrawalNoteDetail: (id: string) =>
        [...packagingLotKeys.withdrawalNotes(), 'detail', id] as const,
    outNotesForPackagingProcess: (search: string) =>
        [
            ...packagingLotKeys.outNotes(),
            'for-packaging-process',
            search,
        ] as const,
}

// ============================================================================
// Lot queries
// ============================================================================

export function usePackagingLots(
    params: ListLotsParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: packagingLotKeys.lotList(params),
        queryFn: () => packagingLotApi.listLots(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function usePackagingLotsInfinite(
    params: Omit<ListLotsParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...packagingLotKeys.lots(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            packagingLotApi.listLots({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePackagingLot(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: packagingLotKeys.lotDetail(id ?? ''),
        queryFn: () => packagingLotApi.getLot(id as string),
    })
}

export function usePackagingLotHistory(id: string | undefined) {
    return useInfiniteQuery({
        enabled: !!id,
        queryKey: [...packagingLotKeys.lots(), 'history', id ?? ''] as const,
        queryFn: ({ pageParam }) =>
            packagingLotApi.getLotHistory(id as string, {
                page: pageParam,
                per_page: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

// ============================================================================
// Stock-in note queries
// ============================================================================

export function usePackagingInNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: packagingLotKeys.inNoteList(params),
        queryFn: () => packagingLotApi.listInNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function usePackagingInNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...packagingLotKeys.inNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            packagingLotApi.listInNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePackagingInNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: packagingLotKeys.inNoteDetail(id ?? ''),
        queryFn: () => packagingLotApi.getInNote(id as string),
    })
}

export function useCreatePackagingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateInNoteRequest) =>
            packagingLotApi.createInNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: packagingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: packagingLotKeys.lots() })
            toast.success(m.pkl_toast_create_pnk())
        },
    })
}

export function useUpdatePackagingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateInNoteRequest }) =>
            packagingLotApi.updateInNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.inNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: packagingLotKeys.lots() })
            toast.success(m.pkl_toast_update_pnk())
        },
    })
}

export function useApprovePackagingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingLotApi.approveInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.inNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: packagingLotKeys.lots() })
            toast.success(m.pkl_toast_approve_pnk())
        },
    })
}

export function useRejectPackagingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            packagingLotApi.rejectInNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.inNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.inNotes() })
            toast.success(m.pkl_toast_reject_pnk())
        },
    })
}

export function useCancelPackagingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingLotApi.cancelInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.inNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.inNotes() })
            toast.success(m.pkl_toast_cancel_pnk())
        },
    })
}

// ============================================================================
// Stock-out note queries
// ============================================================================

export function usePackagingOutNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: packagingLotKeys.outNoteList(params),
        queryFn: () => packagingLotApi.listOutNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function usePackagingOutNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...packagingLotKeys.outNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            packagingLotApi.listOutNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useApprovedPackagingStockOuts(opts: { reason?: string } = {}) {
    return useQuery({
        queryKey: ['packaging-stock-out-notes', 'approved-list', opts],
        queryFn: async () => {
            const res = await packagingLotApi.listOutNotes({
                status: 'approved',
                ...(opts.reason ? { reason: opts.reason } : {}),
                per_page: 200,
            })
            return { items: res.data ?? [] }
        },
    })
}

export function usePackagingOutNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: packagingLotKeys.outNoteDetail(id ?? ''),
        queryFn: () => packagingLotApi.getOutNote(id as string),
    })
}

export function useCreatePackagingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateOutNoteRequest) =>
            packagingLotApi.createOutNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: packagingLotKeys.outNotes() })
            toast.success(m.pkl_toast_create_pxk())
        },
    })
}

export function useUpdatePackagingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateOutNoteRequest }) =>
            packagingLotApi.updateOutNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: packagingLotKeys.lots() })
            toast.success(m.pkl_toast_update_pxk())
        },
    })
}

export function useApprovePackagingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingLotApi.approveOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: packagingLotKeys.lots() })
            toast.success(m.pkl_toast_approve_pxk())
        },
    })
}

export function useRejectPackagingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            packagingLotApi.rejectOutNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.outNotes() })
            toast.success(m.pkl_toast_reject_pxk())
        },
    })
}

export function useCancelPackagingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingLotApi.cancelOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: packagingLotKeys.outNotes() })
            toast.success(m.pkl_toast_cancel_pxk())
        },
    })
}

/**
 * PXK vật tư đã duyệt, lý do "xuất sang đóng gói" — nguồn vật tư (TUỲ CHỌN) cho
 * PNK sản phẩm. Dùng cho picker ở form tạo PNK.
 */
export function usePackagingOutNotesForPackagingProcess(
    search = '',
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: packagingLotKeys.outNotesForPackagingProcess(search),
        queryFn: () =>
            packagingLotApi.listOutNotesForPackagingProcess(
                search || undefined,
            ),
    })
}

// ============================================================================
// Phiếu lấy hàng (withdrawal note) queries
// ============================================================================

export function usePackagingWithdrawalNotes(
    params: ListWithdrawalNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: packagingLotKeys.withdrawalNoteList(params),
        queryFn: () => packagingLotApi.listWithdrawalNotes(params),
    })
}

/** Infinite variant cho mobile "Xem thêm" pattern. */
export function usePackagingWithdrawalNotesInfinite(
    params: Omit<ListWithdrawalNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [
            ...packagingLotKeys.withdrawalNotes(),
            'infinite',
            params,
        ] as const,
        queryFn: ({ pageParam }) =>
            packagingLotApi.listWithdrawalNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function usePackagingWithdrawalNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: packagingLotKeys.withdrawalNoteDetail(id ?? ''),
        queryFn: () => packagingLotApi.getWithdrawalNote(id as string),
    })
}

export function useCreatePackagingWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateWithdrawalNoteRequest) =>
            packagingLotApi.createWithdrawalNote(req),
        onSuccess: () => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.withdrawalNotes(),
            })
            toast.success(m.pkl_toast_create_plh())
        },
    })
}

export function useCancelPackagingWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingLotApi.cancelWithdrawalNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: packagingLotKeys.withdrawalNoteDetail(id),
            })
            qc.invalidateQueries({
                queryKey: packagingLotKeys.withdrawalNotes(),
            })
            toast.success(m.pkl_toast_cancel_plh())
        },
    })
}
